import React, { useEffect, useRef, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Tooltip,
  Checkbox,
  Button,
  Box,
  Typography,
  Theme,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DownloadIcon from '@mui/icons-material/Download';
import { InvoiceItem } from '../config/types';

interface Column {
  id: keyof InvoiceItem | 'actions';
  label: string;
  minWidth: number;
  format?: (value: any) => string;
}

interface DataPreviewTableProps {
  entries: InvoiceItem[];
  onDeleteEntry: (index: number) => void;
  onEditEntry: (index: number) => void;
  onDownloadInvoice: (entry: InvoiceItem) => Promise<void>;
  onDownloadPdf: (entry: InvoiceItem) => Promise<void>;
  /** Zips every invoice's Excel and PDF, reporting progress as it goes */
  onDownloadAll: (onProgress: (done: number, total: number) => void) => Promise<void>;
  onSplitEntries: (selectedIndices: number[]) => void;
}

const DataPreviewTable: React.FC<DataPreviewTableProps> = ({
  entries,
  onDeleteEntry,
  onEditEntry,
  onDownloadInvoice,
  onDownloadPdf,
  onDownloadAll,
  onSplitEntries
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  // Indices currently checked for the next split (transient; locked members are
  // tracked on the entries themselves via splitId).
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Set while the zip is being built: { done, total }.
  const [downloadProgress, setDownloadProgress] = useState<{ done: number; total: number } | null>(
    null
  );
  const invoiceCount = entries.filter(entry => !entry.isSplit).length;

  useEffect(() => {
    if (tableContainerRef.current && entries.length > 0) {
      tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
    }
  }, [entries]);

  // Clear transient selection whenever the number of rows changes (add/delete/split).
  useEffect(() => {
    setSelected(new Set());
  }, [entries.length]);

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });

  const columns: Column[] = [
    { id: 'rowNumber', label: 'Row #', minWidth: 70 },
    { id: 'destination', label: 'Destination', minWidth: 150 },
    { id: 'date', label: 'Date', minWidth: 100 },
    { id: 'bolNumber', label: 'BOL Number', minWidth: 120 },
    { id: 'invoiceNumber', label: 'Invoice Number', minWidth: 120 },
    { id: 'orderNumber', label: 'Order Number', minWidth: 120 },
    { id: 'loadNumber', label: 'Load Number', minWidth: 120 },
    { id: 'terminal', label: 'Terminal', minWidth: 120 },
    { id: 'gross', label: 'Gross', minWidth: 100 },
    { id: 'net', label: 'Net', minWidth: 100 },
    { id: 'rate', label: 'Rate', minWidth: 100 },
    { id: 'freightRev', label: 'Freight Rev', minWidth: 120, format: (value) => currencyFormatter.format(value) },
    { id: 'fuelSurcharge', label: 'Fuel Surcharge', minWidth: 120, format: (value) => currencyFormatter.format(value) },
    { id: 'totalDem', label: 'Total DEM', minWidth: 120 },
    { id: 'demurrageCharge', label: 'Demurrage Charge', minWidth: 120, format: (value) => currencyFormatter.format(value) },
    { id: 'totalCharges', label: 'Total Charges', minWidth: 120, format: (value) => currencyFormatter.format(value) },
    { id: 'notes', label: 'Comments', minWidth: 200 },
    { id: 'actions', label: 'Actions', minWidth: 150 }
  ];

  const toggleSelect = (index: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSplit = () => {
    if (selected.size < 2) return;
    onSplitEntries(Array.from(selected));
    setSelected(new Set());
  };

  const handleDownloadAll = async () => {
    setDownloadProgress({ done: 0, total: invoiceCount });
    try {
      await onDownloadAll((done, total) => setDownloadProgress({ done, total }));
    } finally {
      setDownloadProgress(null);
    }
  };

  return (
    <Paper sx={{
      width: '100%',
      overflow: 'hidden',
      maxWidth: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Split toolbar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: { xs: 1, sm: 2 },
        px: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="body2" color="text.secondary">
          {selected.size > 0
            ? `${selected.size} load${selected.size === 1 ? '' : 's'} selected`
            : 'Select 2 or more loads to split'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
          <Tooltip title="Download every invoice as Excel and PDF in one zip">
            <span>
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={
                  downloadProgress ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
                }
                disabled={invoiceCount === 0 || downloadProgress !== null}
                onClick={handleDownloadAll}
              >
                {downloadProgress
                  ? `Preparing ${downloadProgress.done}/${downloadProgress.total}...`
                  : `Download All (${invoiceCount})`}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<CallSplitIcon />}
            disabled={selected.size < 2}
            onClick={handleSplit}
          >
            Split Selected
          </Button>
        </Box>
      </Box>

      <TableContainer
        ref={tableContainerRef}
        sx={{
          flexGrow: 1,
          maxHeight: '85vh',
          overflowX: 'auto',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            height: 8,
            width: 8
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f5f5f5'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: 4
          }
        }}
      >
        <Table stickyHeader sx={{
          minWidth: 2260,
          tableLayout: 'fixed'
        }}>
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: (theme: Theme) => theme.palette.background.paper,
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  width: 60
                }}
              />
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    minWidth: column.minWidth,
                    backgroundColor: (theme: Theme) => theme.palette.background.paper,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    width: column.minWidth,
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    py: 2
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, index) => {
              const isSplit = !!entry.isSplit;
              const isMember = !!entry.splitId && !isSplit;
              const isChecked = isMember || selected.has(index);
              return (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={index}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: (theme: Theme) => theme.palette.action.hover
                    },
                    ...(isSplit && {
                      backgroundColor: (theme: Theme) => theme.palette.warning.light,
                      '&:nth-of-type(odd)': {
                        backgroundColor: (theme: Theme) => theme.palette.warning.light
                      }
                    })
                  }}
                >
                  <TableCell padding="checkbox" sx={{ width: 60 }}>
                    {!isSplit && (
                      <Tooltip title={isMember ? 'Part of a split — delete the SPLIT row to unselect' : ''}>
                        <span>
                          <Checkbox
                            checked={isChecked}
                            disabled={isMember}
                            onChange={() => toggleSelect(index)}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                  {columns.slice(0, -1).map((column) => {
                    const value = entry[column.id as keyof InvoiceItem];
                    return (
                      <TableCell
                        key={column.id}
                        sx={{
                          width: column.minWidth,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          py: 1.5,
                          ...(isSplit && { fontWeight: 'bold' })
                        }}
                      >
                        {column.format ? column.format(value) : value}
                      </TableCell>
                    );
                  })}
                  <TableCell sx={{
                    position: 'sticky',
                    right: 0,
                    backgroundColor: isSplit ? 'warning.light' : 'white',
                    zIndex: 1,
                    width: 150,
                    py: 1
                  }}>
                    <Stack direction="row" spacing={1}>
                      {/* Split entries can only be deleted — not edited or downloaded. */}
                      {!isSplit && (
                        <>
                          <Tooltip title="Edit Entry">
                            <IconButton
                              size="small"
                              onClick={() => onEditEntry(index)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download Invoice">
                            <IconButton
                              size="small"
                              onClick={() => onDownloadInvoice(entry)}
                              color="info"
                            >
                              <ReceiptIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download PDF">
                            <IconButton
                              size="small"
                              onClick={() => onDownloadPdf(entry)}
                              color="secondary"
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={isSplit ? 'Delete Split' : 'Delete Entry'}>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteEntry(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DataPreviewTable;
